import { CustomerInfo } from '@/domain/CustomerInfo'
import { AgencyInfo } from '@/domain/AgencyInfo'
import { UserInfo } from '@/domain/UserInfo'
import { InspectionInfo } from '@/domain/InspectionInfo'
import { PriceInfo } from '@/domain/PriceInfo'
import { SumInfo } from '@/domain/SumInfo'
import FileData from '@/domain/FileData'
import DateUtils from '@/utils/DateUtils'

/**
 * 請書明細
 */
export class ConfirmationDetail {
    /** 受付番号 */
    requestNo = ""
    /** 検体名称 */
    specimenName = ""
    /** 検査情報 */
    inspectionInfo = new InspectionInfo()
    /* 価格情報 */
    priceInfo = new PriceInfo()

    /**
     * コンストラクタ
     * @param init 
     */
    constructor(init?: Partial<ConfirmationDetail>) {
        Object.assign(this, init)
    }
}

/**
 * 請書
 */
export class Confirmation {
    /** 請書番号 */
    confirmationNo = ""
    /** 件名 */
    subject = ""
    /** 検査依頼種別 */
    requestType = 0
    /** 作成者 */
    authorUser = new UserInfo()
    /** 承認者 */
    authorizerUser = new UserInfo()
    /** 発行日 YYYY-MM-DD */
    confirmationOn = ""
    /** 納品予定日 YYYY-MM-DD */
    deliveryOn = ""
    /** 顧客情報 */
    customerInfo = new CustomerInfo()
    /** 代理店 */
    agencyInfo = new AgencyInfo()
    /** 検体名称(複数) */
    specimenNames = ""
    /** 検体識別記号(複数) */
    specimenIds = ""
    /** 集計情報 */
    sumInfo = new SumInfo()
    /** 備考 */
    note = ""
    /** メール送信日時 */
    mailSendAt: string | null = null
    /** 印刷日時 */
    printAt: string | null = null

    /** 請書明細 */
    details = [new ConfirmationDetail()]

    /** 請書書 */
    confirmationFile: FileData = new FileData()

    /** 受付番号 */
    requestNos: string[] = []

    /** 前の請書番号 */
    previousConfirmationNo = ""

    /** 次の請書番号 */
    nextConfirmationNo = ""

    /**
     * コンストラクタ
     * @param init 
     */
    constructor(init?: Partial<Confirmation>) {
        Object.assign(this, init)
    }

    /**
     * 複製を作成する
     */
    clone() {
        return new Confirmation(JSON.parse(JSON.stringify(this)));
    }

    /**
     * 宛先の会社が空か
     */
    get isEmptyCustomer() {
        return this.customerInfo.customerPrefectureCode === "" || this.customerInfo.customerCode === "" || this.customerInfo.customerBranchNumber === ""
    }

    /**
     * 代理店経由か
     */
    get hasAgency() {
        return this.agencyInfo.agencyPrefectureCode !== null && this.agencyInfo.agencyCode !== null || this.agencyInfo.agencyBranchNumber !== null
    }

    /**
     * メール送信済みか？
     */
    get isMailSended() {
        return !(this.mailSendAt === null || this.mailSendAt === "")
    }

    /**
     * 印刷済みか？
     */
    get isPrinted() {
        return !(this.printAt === null || this.printAt === "")
    }

    /**
     * ロック状態(編集不可)か？
     */
    get isLocked() {
        return false
    }

    /**
     * 小計
     */
    get amount() {
        return this.details
            .map(x => x.priceInfo.unitPrice * x.priceInfo.quantity)
            .reduce((p, x) => p + x, 0)
    }

    /**
     * 小計（カンマ区切り）
     */
    get amountText() {
        return isNaN(this.amount) ? "-" : this.amount.toLocaleString()
    }

    /**
     * 消費税
     */
    get tax() {
        return this.details
            .map(x => Math.floor(x.priceInfo.unitPrice * x.priceInfo.quantity * (x.priceInfo.taxRate / 100)))
            .reduce((p, x) => p + x, 0)
    }

    /**
     * 消費税（カンマ区切り）
     */
    get taxText() {
        return isNaN(this.tax) ? "-" : this.tax.toLocaleString()
    }

    /**
     * 合計金額
     */
    get totalAmount() {
        return this.amount + this.tax
    }

    /**
     * 合計金額（カンマ区切り）
     */
    get totalAmountText() {
        return isNaN(this.totalAmount) ? "-" : this.totalAmount.toLocaleString()
    }

    /**
     * フィルターキーワードの取得
     */
    get filterKeyword() {
        const dateUtils = DateUtils.instance
        return `${dateUtils.toJapanFormat(this.confirmationOn)} ${this.confirmationNo} ${dateUtils.toJapanFormat(this.deliveryOn)} ${this.customerInfo.customerName} ${this.agencyInfo.agencyName} ${this.specimenNames} ${this.specimenIds} ${this.sumInfo.inspectionNames} ${this.sumInfo.classificationNames}`
    }

    /**
     * 明細行を追加
     * @param detail
     */
    addDetailLine(detail = new ConfirmationDetail()) {
        this.details.push(detail)
    }

    /**
     * 明細行を削除
     * @param lineIndex
     */
    deleteDeailLine(lineIndex: number) {
        this.details.splice(lineIndex, 1)
        if (this.details.length === 0) {
            this.addDetailLine()
        }
    }
}

/**
 * メール送信キュー
 */
export class MailQueue {
    /** TO宛先 */
    to = ""
    /** CC宛先 */
    cc: string | null = null
    /** BCC宛先 */
    bcc: string | null = null
    /** REPLY TO  */
    replyTo: string | null = null
    /** 件名 */
    subject = ""
    /** 本文 */
    content = ""
    /** 請書番号 */
    confirmationNos: string[] = []
    /** メール送信日時 */
    mailSendAt: string | null = null

    /**
     * コンストラクタ
     * @param init 
     */
    constructor(init?: Partial<MailQueue>) {
        Object.assign(this, init)
    }
}
